<template>
  <div class="ai-chat-container">
    <div class="chat-header">
      <div class="header-left">
        <button @click="goBack" class="btn-back">
          <i class="fas fa-arrow-left"></i>
        </button>
        <i class="fas fa-microchip"></i>
        <h2>AI助手</h2>
      </div>
      <div class="model-selector">
        <select v-model="selectedModel" @change="resetChat">
          <option value="gpt-4">GPT-4</option>
          <option value="gpt-3.5">GPT-3.5 Turbo</option>
          <option value="deepseek">Deepseek</option>
          <option value="system">系统助手</option>
        </select>
      </div>
      <div class="header-actions">
        <button @click="resetChat" class="btn-reset">
          <i class="fas fa-rotate-right"></i>
        </button>
      </div>
    </div>

    <div class="chat-messages" ref="messagesContainer">
      <div class="welcome-message">
        <div class="welcome-icon">
          <i class="fas fa-robot"></i>
        </div>
        <h3>您好！我是您的AI助手</h3>
        <p>我可以帮助您解答关于系统操作的问题，也可以与您进行日常交流。</p>
        <div class="quick-actions">
          <button v-for="action in quickActions" :key="action.text" @click="sendQuickAction(action)">
            {{ action.text }}
          </button>
        </div>
      </div>

      <div v-for="(message, index) in messages" :key="index" class="message-item" :class="message.role">
        <div class="message-avatar">
          <i v-if="message.role === 'user'" class="fas fa-user"></i>
          <i v-else class="fas fa-robot"></i>
        </div>
        <div class="message-content">
          <div class="message-header">
            <span class="message-role">{{ message.role === 'user' ? '我' : 'AI助手' }}</span>
            <span class="message-time">{{ message.time }}</span>
          </div>
          <div v-if="message.thought" class="thinking-indicator">
            <i class="fas fa-cog fa-spin"></i>
            <span>正在深度思考...</span>
          </div>
          <div class="message-body" v-html="message.content"></div>
        </div>
      </div>
    </div>

    <div class="chat-input-area">
      <div class="input-tools">
        <button class="tool-btn" title="上传文件">
          <i class="fas fa-paperclip"></i>
        </button>
        <button class="tool-btn" title="清除输入">
          <i class="fas fa-trash"></i>
        </button>
      </div>
      <textarea
        v-model="inputMessage"
        @keydown.enter.exact.prevent="sendMessage"
        placeholder="输入您的问题，按 Enter 发送..."
        class="chat-input"
        rows="2"
      ></textarea>
      <button @click="sendMessage" :disabled="!inputMessage.trim() || isLoading" class="send-btn">
        <i v-if="isLoading" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-paper-plane"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const goBack = () => {
  router.push('/dashboard')
}

const messagesContainer = ref(null)
const messages = ref([])
const inputMessage = ref('')
const isLoading = ref(false)
const selectedModel = ref('gpt-4')

const quickActions = [
  { text: '如何创建用户', type: 'system' },
  { text: '如何分配角色权限', type: 'system' },
  { text: '如何查看系统日志', type: 'system' },
  { text: '今天天气怎么样', type: 'general' },
  { text: '讲个笑话', type: 'general' },
  { text: '写一封邮件', type: 'general' }
]

const systemKnowledge = {
  '如何创建用户': '在系统中创建用户的步骤如下：\n\n1. 点击左侧导航栏的「用户中心」\n2. 选择「用户管理」\n3. 点击「新增用户」按钮\n4. 填写用户信息（用户名、密码、邮箱等）\n5. 选择所属组织\n6. 点击「保存」完成创建',
  '如何分配角色权限': '分配角色权限的步骤：\n\n1. 进入「用户中心」->「角色管理」\n2. 选择需要修改的角色\n3. 点击「编辑」按钮\n4. 在右侧成员列表中添加或移除成员\n5. 保存更改即可',
  '如何查看系统日志': '查看系统日志的方法：\n\n1. 点击左侧导航栏的「数据中心」\n2. 选择「日志查询」\n3. 设置查询条件（时间范围、操作类型等）\n4. 点击「查询」查看结果',
  '用户管理': '用户管理模块用于管理系统用户，包括：\n- 创建新用户\n- 修改用户信息\n- 重置用户密码\n- 分配用户角色\n- 管理用户状态',
  '角色管理': '角色管理模块用于管理系统角色，包括：\n- 创建和删除角色\n- 设置角色的上级角色\n- 管理角色成员\n- 批量添加/删除成员',
  '组织管理': '组织管理模块用于管理企业组织结构，包括：\n- 创建组织层级\n- 设置组织成员的职位和职级\n- 指定组织负责人\n- 管理组织架构'
}

const generalResponses = {
  '今天天气怎么样': '抱歉，我无法实时获取天气信息。您可以通过天气应用或网站查询当地天气。',
  '讲个笑话': '为什么程序员总是分不清万圣节和圣诞节？\n\n因为 Oct 31 等于 Dec 25！\n（八进制31等于十进制25）',
  '写一封邮件': '好的，这是一封邮件模板：\n\n---\n主题：【工作汇报】\n\n尊敬的领导：\n\n您好！\n\n特此汇报近期工作进展...\n\n此致\n敬礼\n---\n\n您可以根据具体需求修改内容。'
}

const generateResponse = (question) => {
  const lowerQuestion = question.toLowerCase()
  
  for (const [key, answer] of Object.entries(systemKnowledge)) {
    if (lowerQuestion.includes(key.toLowerCase())) {
      return answer
    }
  }
  
  for (const [key, answer] of Object.entries(generalResponses)) {
    if (lowerQuestion.includes(key.toLowerCase())) {
      return answer
    }
  }
  
  if (lowerQuestion.includes('系统') || lowerQuestion.includes('如何') || lowerQuestion.includes('操作') || lowerQuestion.includes('怎么')) {
    return `关于您的问题："${question}"\n\n我可以为您提供系统操作方面的帮助。如果您有具体的操作问题，请详细描述您想要完成的任务，我会为您提供分步指导。\n\n常见的系统操作包括：用户管理、角色权限、组织架构、日志查询等。`
  }
  
  return `您的问题："${question}"\n\n这是一个很好的问题！我来为您分析一下：\n\n由于我主要专注于本系统的操作指导，如果您有关于系统功能使用的问题，我可以为您提供详细的解答。\n\n对于其他话题，我也可以陪您聊聊。请问您还有什么想了解的吗？`
}

const addMessage = (role, content, thought = false) => {
  messages.value.push({
    role,
    content,
    thought,
    time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  })
}

const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

const sendMessage = async () => {
  if (!inputMessage.value.trim() || isLoading.value) return
  
  const question = inputMessage.value.trim()
  inputMessage.value = ''
  
  addMessage('user', question)
  await scrollToBottom()
  
  isLoading.value = true
  
  addMessage('assistant', '', true)
  await scrollToBottom()
  
  await new Promise(resolve => setTimeout(resolve, 1500))
  
  const lastMsgIndex = messages.value.length - 1
  messages.value[lastMsgIndex].thought = false
  messages.value[lastMsgIndex].content = generateResponse(question)
  
  isLoading.value = false
  await scrollToBottom()
}

const sendQuickAction = (action) => {
  inputMessage.value = action.text
  sendMessage()
}

const resetChat = () => {
  messages.value = []
}
</script>

<style scoped>
.ai-chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-back {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 6px 10px;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
}

.header-left i {
  font-size: 20px;
}

.header-left h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.model-selector select {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 6px 12px;
  color: white;
  font-size: 14px;
  cursor: pointer;
}

.model-selector select option {
  background: #333;
  color: white;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.btn-reset {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 6px 10px;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-reset:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
}

.welcome-message {
  text-align: center;
  padding: 40px 20px;
}

.welcome-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 36px;
}

.welcome-message h3 {
  margin: 0 0 10px;
  font-size: 20px;
  color: #333;
}

.welcome-message p {
  margin: 0 0 20px;
  color: #666;
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.quick-actions button {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-actions button:hover {
  background: #667eea;
  border-color: #667eea;
  color: white;
}

.message-item {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.message-item.user {
  flex-direction: row-reverse;
}

.message-item.user .message-content {
  background: #667eea;
  color: white;
  border-radius: 12px 12px 0 12px;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message-item.user .message-avatar {
  background: #667eea;
  color: white;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  background: white;
  border-radius: 12px 12px 12px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #999;
}

.message-item.user .message-header {
  color: rgba(255, 255, 255, 0.8);
}

.message-body {
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.thinking-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #667eea;
  font-size: 14px;
}

.chat-input-area {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 16px 20px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.input-tools {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tool-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.tool-btn:hover {
  background: #f5f5f5;
  border-color: #667eea;
  color: #667eea;
}

.chat-input {
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  resize: none;
  outline: none;
  transition: border-color 0.2s;
}

.chat-input:focus {
  border-color: #667eea;
}

.chat-input::placeholder {
  color: #999;
}

.send-btn {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
